import { useState, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { Cookies } from 'react-cookie'
import axios from 'axios'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../../context/auth-context'
import { useFetchCities } from '../../hooks/useFetchIranCities'
import { useFetchProvinces } from '../../hooks/useFetchProvinces'
import { makeNumbersFarsi, toEnglishDigits } from '../../utils/farsi'
import { API_BASE_URL } from 'config/api'

interface IFormInput {
  customerFirstName: string
  customerLastName: string
  customerTelephone: number | string
  address: string
  postalCode: number | string
}

export const ShippingInfoForm = () => {
  const { user } = useAuth()
  const cookies = new Cookies()
  const savedProvinceId =
    Number(localStorage.getItem('shippingProvinceId')) || 1
  const savedCityId = Number(localStorage.getItem('shippingCityId')) || 1
  const [provinceId, setProvinceId] = useState(savedProvinceId)
  const [cityId, setCityId] = useState(savedCityId)
  const { cities } = useFetchCities()
  const { provinces } = useFetchProvinces()

  const location = useLocation()
  const orderId = location.state.orderId

  useEffect(() => {
    localStorage.setItem('shippingProvinceId', String(provinceId))
  }, [provinceId])
  useEffect(() => {
    localStorage.setItem('shippingCityId', String(cityId))
  }, [cityId])

  const handleProvinceIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProvinceId = parseInt(e.target.value)
    setProvinceId(newProvinceId)
    const provinceCities = getProvinceCityIds(newProvinceId)
    if (provinceCities && provinceCities.length > 0) {
      setCityId(provinceCities[0].id)
    }
  }

  const handleCityIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCityId(parseInt(e.target.value))
  }

  const getProvinceCityIds = (provinceId: number) => {
    return cities?.filter((city) => city.provinceId === provinceId)
  }

  let provinceCityIds = getProvinceCityIds(provinceId)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<IFormInput>()
  const formValues = watch()

  useEffect(() => {
    const hasValue = Object.values(formValues).some((v) => v && v !== '')
    if (hasValue) {
      localStorage.setItem('shippingForm', JSON.stringify(formValues))
    }
  }, [formValues])

  useEffect(() => {
    const saved = localStorage.getItem('shippingForm')
    if (saved) {
      const parsed = JSON.parse(saved)
      const hasValue = Object.values(parsed).some((v) => v && v !== '')
      if (hasValue) {
        reset(parsed)
      }
    }
    setFocus('customerFirstName')
  }, [reset, setFocus])

  const onSubmit: SubmitHandler<IFormInput> = async (options) => {
    const token = cookies.get('@bookstory-auth-token')
    const authToken = token?.user?.authToken
    let userToken = authToken?.accessToken

    let userData = {
      cellphone: user.cellphone,
      firstName: user.firstName,
      lastName: user.lastName,
    }

    const result = await axios
      .put(`${API_BASE_URL}/orders/${orderId}/customer`, userData)
      .then((res) => res.data)

    if (result) {
      let data = {
        customerFirstName: options.customerFirstName,
        customerLastName: options.customerLastName,
        customerTelephone: toEnglishDigits(options.customerTelephone as string),
        text: toEnglishDigits(options.address),
        postalCode: toEnglishDigits(options.postalCode as string),
        cityId,
      }

      const config = {
        method: 'PUT',
        data: data,
        url: `${API_BASE_URL}/orders/${orderId}/shipping`,
        headers: {
          Authorization: 'bearer ' + userToken,
        },
      }
      await axios(config).then((res) => {
        if (res.status === 200) {
          localStorage.setItem('cart', JSON.stringify(res.data))
          navigate('/cart/payment')
        }
      })
    }
  }

  useEffect(() => {
    setFocus('customerFirstName')
  }, [setFocus])

  const handleBackToSettingsClick = () => {
    navigate('/cart')
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('customerTelephone', toEnglishDigits(e.target.value))
  }

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('postalCode', toEnglishDigits(e.target.value))
  }

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('address', toEnglishDigits(e.target.value))
  }

  return (
    <div className='max-w-lg my-10 mx-auto flex flex-col border py-8 px-4 rounded-md' style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          اطلاعات آدرس گیرنده
        </title>
        <meta
          name='description'
          content='داستان کتاب - داستان ها همیشه در دل و ذهن می مانند'
        />
      </Helmet>
      <button
        className='rounded border-none w-full p-2 text-white cursor-pointer'
        style={{ backgroundColor: 'var(--accent-primary)' }}
        onClick={handleBackToSettingsClick}
      >
        بازگشت به سبد خرید
      </button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col items-start my-4'>
          <label className='text-lg text-right font-medium mb-2'>
            {' '}
            استان:{' '}
          </label>{' '}
          <select
            className='rounded border-none w-full p-2 text-white cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
            value={provinceId}
            onChange={handleProvinceIdChange}
          >
            {provinces?.map((province) => {
              return (
                <option
                  className='hover:bg-[#81A1C1]'
                  key={province.id}
                  value={province.id}
                >
                  {province.title}
                </option>
              )
            })}
          </select>
        </div>
        <div className='flex flex-col items-start my-4'>
          <label className='text-lg text-right font-medium mb-2'>شهر: </label>
          <select
            className='rounded border-none w-full p-2 text-white cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
            value={cityId}
            onChange={handleCityIdChange}
          >
            {provinceCityIds?.map((city) => {
              return (
                <option
                  className='cursor-pointer'
                  key={city.id}
                  value={city.id}
                >
                  {city.title}
                </option>
              )
            })}
          </select>
        </div>
        <h1 className='text-lg text-right font-medium mt-4 mb-2'>
          نام گیرنده:
        </h1>
        <input
          className='input-theme block outline-none w-full rounded border p-2 mb-2.5 text-base'
          type='text'
          {...register('customerFirstName', {
            required: 'first name is required',
          })}
        />
        {errors.customerFirstName && (
          <p className='text-red-600 before:inline before:content-["⚠"]'>
            {' '}
            {errors.customerFirstName.message}
          </p>
        )}

        <h1 className='text-lg text-right font-medium mt-4 mb-2'>
          نام خانوادگی گیرنده:
        </h1>

        <input
          className='input-theme block outline-none w-full rounded border p-2 mb-2.5 text-base'
          type='text'
          {...register('customerLastName', {
            required: 'last name is required',
          })}
        />
        {errors.customerLastName && (
          <p className='text-red-600 before:inline before:content-["⚠"]'>
            {' '}
            {errors.customerLastName.message}
          </p>
        )}
        <h1 className='text-lg text-right font-medium mt-4 mb-2'>
          شماره تلفن:{' '}
        </h1>

        <input
          type='text'
          className='input-theme block outline-none w-full rounded border p-2 mb-2.5 text-base'
          {...register('customerTelephone', {
            maxLength: 13,
            onChange: handlePhoneNumberChange,
          })}
          dir='ltr'
          value={makeNumbersFarsi(watch('customerTelephone') || '')}
        />
        {errors.customerTelephone && (
          <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
            {' '}
            Phone number is not correct format
          </p>
        )}

        <h1 className='text-lg text-right font-medium mt-4 mb-2'>آدرس: </h1>

        <textarea
          className='input-theme block h-auto outline-none w-full rounded border p-2 mb-2.5 text-base'
          {...register('address', {
            onChange: handleAddressChange,
          })}
          value={makeNumbersFarsi(watch('address') || '')}
        />
        {errors.address && (
          <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
            {' '}
            Address is required
          </p>
        )}

        <h1 className='text-lg text-right font-medium mt-4 mb-2'>کد پستی: </h1>
        <input
          className='input-theme block outline-none w-full rounded border p-2 mb-2.5 text-base'
          type='text'
          {...register('postalCode', {
            minLength: 10,
            maxLength: 10,
            onChange: handlePostalCodeChange,
          })}
          dir='ltr'
          value={makeNumbersFarsi(watch('postalCode') || '')}
        />
        {errors.postalCode && (
          <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
            {' '}
            postal code is required
          </p>
        )}
        <input
          className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value='ادامه'
        />
      </form>
    </div>
  )
}
